import { AnimatePresence, m as motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, FileText, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { StatusPill } from '@/components/ui/StatusPill';
import { toast, useDocumentDrawer } from '@/stores/ui';
import { documentsService } from '@/services/documents';
import { useDialogA11y } from '@/lib/useDialogA11y';

/** Right-side detail drawer — review a document without leaving the page. */
export function DocumentDrawer() {
  const { t } = useTranslation();
  const { doc, open, close } = useDocumentDrawer();
  const dialogRef = useDialogA11y<HTMLElement>(!!doc, close);

  // VLT-362: veredicto REAL — cambia el estado del documento en la base
  // (verified/rejected; el trigger de auditoría lo registra) y el cajón
  // refleja el resultado sin fingir nada.
  const act = async (outcome: 'verified' | 'rejected', messageKey: string) => {
    if (!doc) return;
    const ok = await documentsService.review(doc.id, outcome);
    if (!ok) {
      toast(t('errors.generic.what'), 'info');
      return;
    }
    toast(t(messageKey, { title: doc.title }));
    open(doc.id); // recarga el documento con su veredicto registrado
  };

  // VLT-264: mover el documento de carpeta — escribe documents.tax_year real
  // y recarga el cajón para reflejar lo que quedó guardado.
  const setYear = async (value: string) => {
    if (!doc) return;
    const ok = await documentsService.setTaxYear(doc.id, value ? Number(value) : null);
    if (!ok) {
      toast(t('errors.generic.what'), 'info');
      return;
    }
    toast(t('drawer.yearSaved'));
    open(doc.id);
  };
  const nowYear = new Date().getFullYear();
  const YEARS = Array.from({ length: nowYear + 1 - 2018 + 1 }, (_, i) => String(nowYear + 1 - i));

  const rows: [string, string][] = doc
    ? [
        [t('drawer.client'), doc.client],
        [t('drawer.size'), `${doc.sizeLabel} · ${doc.mime}`],
        [t('drawer.integrity'), doc.hash],
        [t('drawer.source'), doc.source],
        [t('drawer.uploadedAt'), doc.uploadedAt],
      ]
    : [];

  return (
    <AnimatePresence>
      {doc && (
        <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label={doc.title}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="absolute inset-0 bg-background/60 backdrop-blur-sm"
          />
          <motion.aside
            ref={dialogRef}
            initial={{ x: 48, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 48, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="glass-strong absolute inset-y-0 right-0 flex w-[min(26rem,94vw)] flex-col gap-4 overflow-y-auto p-5"
          >
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-primary/10 text-primary">
                <FileText className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="truncate font-display text-base font-semibold">{doc.title}</h2>
                <div className="mt-1"><StatusPill status={doc.status} /></div>
              </div>
              <button
                type="button"
                aria-label={t('drawer.close')}
                onClick={close}
                className="focus-ring rounded-lg p-1.5 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid h-40 place-items-center rounded-xl border border-dashed border-border text-xs text-muted-foreground">
              {t('drawer.preview')}
            </div>

            <dl className="divide-y divide-border/60 rounded-xl border border-border/60">
              <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 text-sm">
                <dt className="text-muted-foreground">
                  <label htmlFor="doc-tax-year">{t('drawer.fiscalYear')}</label>
                </dt>
                <dd className="text-right font-medium">
                  {/* VLT-264: carpeta de año — editable, escribe tax_year real */}
                  <select
                    id="doc-tax-year"
                    value={/^20\d{2}$/.test(doc.fiscalYear) ? doc.fiscalYear : ''}
                    onChange={(e) => void setYear(e.target.value)}
                    className="focus-ring cursor-pointer rounded-lg border border-border/60 bg-transparent px-2 py-1 text-sm font-medium"
                  >
                    <option value="">{t('documents.noYear')}</option>
                    {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </dd>
              </div>
              {rows.map(([label, value]) => (
                <div key={label} className="flex items-center justify-between gap-3 px-3.5 py-2.5 text-sm">
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="truncate text-right font-medium">{value}</dd>
                </div>
              ))}
            </dl>

            {doc.review ? (
              <p className={`mt-auto flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm font-medium ${doc.review.outcome === 'verified' ? 'border-success/40 text-success' : 'border-warning/40 text-warning'}`}>
                {doc.review.outcome === 'verified' ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : <RefreshCw className="h-4 w-4" aria-hidden />}
                {t(doc.review.outcome === 'verified' ? 'drawer.verdictVerified' : 'drawer.verdictReplacement', {
                  date: doc.review.at.slice(0, 10),
                })}
              </p>
            ) : (
              <div className="mt-auto flex gap-2">
                <Button size="sm" className="flex-1" onClick={() => void act('verified', 'drawer.approved')}>
                  <CheckCircle2 className="h-4 w-4" />{t('drawer.approve')}
                </Button>
                <Button size="sm" variant="glass" className="flex-1" onClick={() => void act('rejected', 'drawer.replacementRequested')}>
                  <RefreshCw className="h-4 w-4" />{t('drawer.requestReplacement')}
                </Button>
              </div>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
